import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { loginSchema, type LoginInput } from "@propsys/shared";
import { ArrowRight } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { post } from "@/lib/api";
import { handleFormError, useZodForm } from "@/lib/forms";
import { greeting } from "@/lib/format";
import { qk } from "@/lib/queries";
import type { Me } from "@/lib/types";
import { landingPath } from "@/routes/landing";

export default function Login() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const form = useZodForm(loginSchema, { defaultValues: { email: "", password: "" } });
  const { register, handleSubmit, formState } = form;

  const onSubmit = async (values: LoginInput) => {
    try {
      const me = await post<Me>("/api/auth/login", values);
      qc.setQueryData(qk.me, me);
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next && next.startsWith("/") ? next : landingPath(me.landing), { replace: true });
    } catch (e) {
      handleFormError(form, e);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-3xl">{greeting()} 👋</h1>
      <p className="mt-2 text-muted-foreground">Lépj be, és nézzük, mi újság az ingatlanokkal.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
        <Field label="E-mail cím" htmlFor="email" error={formState.errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" autoFocus placeholder="nev@ceg.hu" aria-invalid={!!formState.errors.email} {...register("email")} />
        </Field>
        <Field label="Jelszó" htmlFor="password" error={formState.errors.password?.message}>
          <PasswordInput id="password" autoComplete="current-password" aria-invalid={!!formState.errors.password} {...register("password")} />
        </Field>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm font-semibold text-primary-text hover:underline">
            Elfelejtetted a jelszavad?
          </Link>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
          Belépés <ArrowRight />
        </Button>
      </form>
      <p className="mt-8 text-center text-sm text-muted-foreground">Regisztrálni nem lehet – a fiókokat meghívóval hozzuk létre.</p>
    </AuthLayout>
  );
}
